"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, CircleAlert, Flame, GitCommitHorizontal as GitCommit, Hammer, PhoneCall, Pipette, ShieldAlert, Trees, Tv, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SewerProblemItem {
  id: string;
  title: string;
  tag: string;
  severity: "High" | "Critical" | "Urgent";
  iconName: "clog" | "backup" | "break" | "roots";
  description: string;
  symptoms: string[];
  immediateRisk: string;
}
const problemsData: SewerProblemItem[] = [{
  id: "clogs",
  title: "Severe Sewer Line Clogs",
  tag: "Most Common",
  severity: "High",
  iconName: "clog",
  description: "Accumulations of hardened cooking grease, heavy mineral scale, flushable wipes, and sanitary debris form dense obstructions inside the main lateral pipe.",
  symptoms: ["Slow drainage across multiple sinks and showers simultaneously", "Gurgling sounds coming from toilet bowls after running water", "Foul sewage odors rising around basement or floor drains"],
  immediateRisk: "Complete wastewater stoppage leading to imminent backflow."
}, {
  id: "backups",
  title: "Main Drain Sewage Backups",
  tag: "Health Hazard",
  severity: "Critical",
  iconName: "backup",
  description: "When waste has nowhere to escape, raw wastewater reverses direction and flows upward into the lowest fixtures, tubs, basements, and floor drains.",
  symptoms: ["Black or brown dirty water surfacing in ground-level bathtubs", "Water bubbling up when washing machine or dishwasher runs", "Standing grey water pooling around basement slab cleanouts"],
  immediateRisk: "Severe property contamination and hazardous biological exposure."
}, {
  id: "breaks",
  title: "Broken & Collapsed Sewer Pipes",
  tag: "Structural Damage",
  severity: "Critical",
  iconName: "break",
  description: "Aged cast iron corrosion, ground settling, heavy vehicle traffic, or shifting Georgia clay causes the underground sewer pipe to crack, displace, or collapse entirely.",
  symptoms: ["Sudden localized soggy patches or sinkholes in the lawn", "Persistent foul sulfur smells outdoors with no visible leak", "Frequent recurring blockages even after conventional snaking"],
  immediateRisk: "Foundation destabilization, soil erosion, and yard excavation needs."
}, {
  id: "root-intrusion",
  title: "Invasive Tree Root Intrusion",
  tag: "Progressive Damage",
  severity: "Urgent",
  iconName: "roots",
  description: "Microscopic tree and shrub root systems detect warmth and moisture, penetrating microscopic pipe joints and growing into dense root balls that trap solid waste.",
  symptoms: ["Periodic line blockages that worsen every spring and summer", "Unusually lush, dark green grass patches directly over sewer route", "Complete stoppage with toilet paper or debris caught on roots"],
  immediateRisk: "Complete pipe fracture and massive physical obstruction."
}];
const diagnosticSteps = [{
  step: "01",
  title: "High-Definition Video Inspection",
  detail: "We feed an illuminated optical camera through your cleanout to pinpoint the exact depth and nature of the problem."
}, {
  step: "02",
  title: "Targeted Clear or Hydro-Jetting",
  detail: "Using 4000 PSI hydro-jetting or motorized augers, we obliterate grease mats, scale buildup, and intrusive root masses."
}, {
  step: "03",
  title: "Trenchless or Spot Repair Plan",
  detail: "If structural damage is found, we evaluate epoxy pipe relining or spot repairs to restore flow with minimal digging."
}];
export default function SewerProblems() {
  const shouldReduceMotion = useReducedMotion();
  const getProblemIcon = (iconName: SewerProblemItem["iconName"]) => {
    switch (iconName) {
      case "clog":
        return <Pipette className="size-6 text-accent" />;
      case "backup":
        return <Waves className="size-6 text-destructive" />;
      case "break":
        return <Hammer className="size-6 text-chart-3" />;
      case "roots":
        return <Trees className="size-6 text-chart-4" />;
      default:
        return <Wrench className="size-6 text-accent" />;
    }
  };
  return <section id="sewerlineproblems" data-nav="light" className="relative w-full bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-20">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase bg-secondary text-secondary-foreground border-border">
            <ShieldAlert className="size-3.5 mr-1 text-accent" />
            Underground Line Diagnostics
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Common Sewer Line Problems in Jonesboro
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            A failing sewer line can quickly lead to contaminated living areas and structural damage. Identify the warning signs before a minor clog turns into a major emergency.
          </p>
        </div>

        {/* 4 Main Sewer Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {problemsData.map((problem, index) => <div key={problem.id} data-index={index} className="h-full">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.45,
            delay: index * 0.1
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-3 rounded-lg bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                          {getProblemIcon(problem.iconName)}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs font-semibold">
                            {problem.tag}
                          </Badge>
                          <Badge variant={problem.severity === "Critical" ? "destructive" : "default"} className="text-xs font-semibold">
                            {problem.severity}
                          </Badge>
                        </div>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground">
                        {problem.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        {problem.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-2 pb-4">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
                          Telltale Symptoms
                        </h4>
                        <ul className="space-y-2">
                          {problem.symptoms.map((symptom, sIdx) => <li key={sIdx} className="flex items-start gap-2.5 text-sm text-card-foreground">
                              <CircleAlert className="size-4 shrink-0 mt-0.5 text-accent" />
                              <span>{symptom}</span>
                            </li>)}
                        </ul>
                      </div>

                      <div className="p-3 rounded-md bg-muted/60 border border-border text-xs text-muted-foreground flex items-start gap-2">
                        <AlertTriangle className="size-4 shrink-0 text-destructive mt-0.5" />
                        <div>
                          <strong className="text-card-foreground font-semibold">Risk: </strong>
                          {problem.immediateRisk}
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" className="w-full justify-center">
                      <Link to="/contact/">
                        Request Camera Inspection
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Diagnostic Process Section */}
        <div className="mt-16 sm:mt-24 p-6 sm:p-10 rounded-2xl bg-secondary text-secondary-foreground border border-border">
          <div className="max-w-3xl mb-8">
            <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground inline-block mb-1">
              Accurate Diagnosis First
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              How We Solve Hidden Sewer Pipe Failures
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              We never guess with your underground lines. Our local Jonesboro team inspects the inside of your pipes before recommending a single repair.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {diagnosticSteps.map((step, idx) => <div key={step.step} data-index={idx} className="bg-card text-card-foreground p-5 rounded-xl border border-border flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl font-extrabold text-primary font-mono">{step.step}</span>
                    <Tv className="size-4 text-muted-foreground" />
                  </div>
                  <h4 className="font-semibold text-base text-card-foreground mb-1.5">{step.title}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{step.detail}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-8 pt-6 border-t border-border/80 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shrink-0">
                <Flame className="size-5" />
              </div>
              <div>
                <h5 className="font-bold text-sm text-foreground">Immediate Sewer Backflow Right Now?</h5>
                <p className="text-xs text-muted-foreground">Our 24/7 emergency dispatch responds day and night in Clayton County.</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
              <Button asChild variant="destructive" className="w-full sm:w-auto">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call Emergency Team</span>
                </a>
              </Button>
              <Button asChild variant="default" className="w-full sm:w-auto">
                <Link to="/problems/">
                  View All Problems
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}